/**
 * src/services/entitiesService.ts
 *
 * Client bindings for /api/entities/* and /api/lists/*.  Keeps a small
 * in-memory cache of the entity list so EntityPanel can render
 * synchronously while background fetches refresh it.
 */

export type EntityType =
  | 'browser_device' | 'vehicle' | 'drone' | 'ground_control_station' | 'operator'
  | 'sensor' | 'virtual' | 'other';

export interface EntityPublicKey {
  pkHash: string;
  pkBytes?: string | null;
  role: 'signer' | 'viewer' | 'telemetry_signer';
  source: 'passkey' | 'mavlink_attested' | 'external';
  verification: 'unverified' | 'verified' | 'revoked';
  registeredAt: number;
}

export interface EntityOperator {
  address: string;
  role: 'operator' | 'viewer' | 'manager';
  delegatedAt: number;
}

/**
 * Slice 7: FAA Remote ID + registration metadata captured per
 * `server/services/faaComplianceService.js`. All fields are publicly
 * displayable per 14 CFR Part 89 — the marker popup shows them so
 * observers can see what the operator self-attests.
 */
export interface EntityFaaCompliance {
  registrationNumber: string;
  remoteIdSerial: string | null;
  remoteIdType: 'standard' | 'module' | 'fria_only' | 'none';
  remoteIdMethod: 'bluetooth' | 'wifi' | 'ant' | 'cellular' | 'none';
  declaredAt: number;
  declaredBy: string;
  attestationHash: string;
  verifiedAt: number | null;
  verificationSource: 'self-attested' | 'geolink-verified' | 'faa-direct';
  /** Present when GeoLink §13.7 lookup ran (see `geolink-faa-registry-handoff.md`). */
  geolinkLookup?: {
    registered: boolean;
    lastVerified: string | null;
    source: string | null;
    note: string | null;
  } | null;
}

export interface EntityRecord {
  id: string;
  entityType: EntityType;
  displayName: string;
  systemId: number;
  componentId: number;
  autopilotType: number;
  vehicleType: number;
  firmwareType: number;
  parentEntityId: string | null;
  operatorEntityId: string | null;
  metadataHash: string | null;
  wallet: string | null;
  publicKeys: EntityPublicKey[];
  operators: EntityOperator[];
  status: string;
  createdAt: number;
  updatedAt: number;
  /** Slice 7 — operator-attested FAA Remote ID + registration. */
  faaCompliance?: EntityFaaCompliance | null;
}

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${DEFAULT_BACKEND}${path}`, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      ...((init?.headers as Record<string, string>) || {}),
    },
  });
  const data = await resp.json().catch(() => ({} as any));
  if (!resp.ok || data.ok === false) {
    throw new Error(data.error || `request_failed_${resp.status}`);
  }
  return data as T;
}

export async function listEntities(params: { type?: EntityType; wallet?: string } = {}): Promise<EntityRecord[]> {
  const query = new URLSearchParams();
  if (params.type)   query.set('type', params.type);
  if (params.wallet) query.set('wallet', params.wallet);
  const { entities } = await request<{ entities: EntityRecord[] }>(
    `/api/entities?${query.toString()}`,
  );
  return entities;
}

export async function getEntity(id: string): Promise<EntityRecord | null> {
  try {
    const { entity } = await request<{ entity: EntityRecord }>(`/api/entities/${encodeURIComponent(id)}`);
    return entity;
  } catch (err: any) {
    if (err?.message === 'not_found') return null;
    throw err;
  }
}

export async function registerEntity(input: Partial<EntityRecord>): Promise<EntityRecord> {
  const { entity } = await request<{ entity: EntityRecord }>(`/api/entities`, {
    method: 'POST', body: JSON.stringify(input),
  });
  return entity;
}

export async function deleteEntity(id: string): Promise<void> {
  await request<{ ok: true }>(`/api/entities/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function linkWallet(id: string, wallet: string): Promise<EntityRecord> {
  const { entity } = await request<{ entity: EntityRecord }>(
    `/api/entities/${encodeURIComponent(id)}/link-wallet`,
    { method: 'POST', body: JSON.stringify({ wallet }) },
  );
  return entity;
}

export async function unlinkWallet(id: string): Promise<EntityRecord> {
  const { entity } = await request<{ entity: EntityRecord }>(
    `/api/entities/${encodeURIComponent(id)}/unlink-wallet`,
    { method: 'POST' },
  );
  return entity;
}

export async function addPublicKey(id: string, key: Partial<EntityPublicKey>): Promise<EntityRecord> {
  const { entity } = await request<{ entity: EntityRecord }>(
    `/api/entities/${encodeURIComponent(id)}/keys`,
    { method: 'POST', body: JSON.stringify(key) },
  );
  return entity;
}

export async function revokePublicKey(id: string, pkHash: string): Promise<EntityRecord> {
  const { entity } = await request<{ entity: EntityRecord }>(
    `/api/entities/${encodeURIComponent(id)}/keys/${encodeURIComponent(pkHash)}`,
    { method: 'DELETE' },
  );
  return entity;
}

export async function addOperator(id: string, op: Partial<EntityOperator>): Promise<EntityRecord> {
  const { entity } = await request<{ entity: EntityRecord }>(
    `/api/entities/${encodeURIComponent(id)}/operators`,
    { method: 'POST', body: JSON.stringify(op) },
  );
  return entity;
}

export async function revokeOperator(id: string, address: string): Promise<EntityRecord> {
  const { entity } = await request<{ entity: EntityRecord }>(
    `/api/entities/${encodeURIComponent(id)}/operators/${encodeURIComponent(address)}`,
    { method: 'DELETE' },
  );
  return entity;
}

export async function appendTelemetry(id: string, body: any): Promise<any> {
  const { telemetry } = await request<{ telemetry: any }>(
    `/api/entities/${encodeURIComponent(id)}/telemetry`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return telemetry;
}

export async function getLatestTelemetry(id: string): Promise<any> {
  const { telemetry } = await request<{ telemetry: any }>(
    `/api/entities/${encodeURIComponent(id)}/telemetry/latest`,
  );
  return telemetry;
}

// ── Lists ────────────────────────────────────────────────────────────────────

export interface ListEntry {
  subject: string;
  kind: number;          // 1 = watch, 2 = deny
  reason: string;
  addedAt: number;
  expiresAt: number;
  scope: 'global' | 'country';
  country?: number;
}

export const LIST_KIND = { WATCH: 1, DENY: 2 } as const;
export const LIST_STATUS = {
  CLEAR: 0, WATCH_COUNTRY: 1, WATCH_GLOBAL: 2, DENY_COUNTRY: 3, DENY_GLOBAL: 4,
} as const;

export async function searchLists(params: { country?: number | 'global'; q?: string; kind?: number } = {}): Promise<ListEntry[]> {
  const query = new URLSearchParams();
  if (params.country != null) query.set('country', String(params.country));
  if (params.q)                query.set('q', params.q);
  if (params.kind != null)     query.set('kind', String(params.kind));
  const { entries } = await request<{ entries: ListEntry[] }>(`/api/lists/search?${query.toString()}`);
  return entries;
}

export async function addListEntry(scope: 'global' | 'country', subject: string, opts: { kind: number; reason?: string; country?: number; expiresAt?: number }): Promise<ListEntry> {
  if (scope === 'global') {
    const { entry } = await request<{ entry: ListEntry }>(`/api/lists/global`, {
      method: 'POST', body: JSON.stringify({ subject, kind: opts.kind, reason: opts.reason, expiresAt: opts.expiresAt }),
    });
    return entry;
  }
  if (opts.country == null) throw new Error('country_required_for_country_scope');
  const { entry } = await request<{ entry: ListEntry }>(`/api/lists/country/${opts.country}`, {
    method: 'POST', body: JSON.stringify({ subject, kind: opts.kind, reason: opts.reason, expiresAt: opts.expiresAt }),
  });
  return entry;
}

export async function removeListEntry(scope: 'global' | 'country', subject: string, country?: number): Promise<void> {
  if (scope === 'global') {
    await request(`/api/lists/global/${encodeURIComponent(subject)}`, { method: 'DELETE' });
    return;
  }
  if (country == null) throw new Error('country_required_for_country_scope');
  await request(`/api/lists/country/${country}/${encodeURIComponent(subject)}`, { method: 'DELETE' });
}

export async function effectiveStatus(subject: string, country?: number): Promise<number> {
  const q = country != null ? `?country=${country}` : '';
  const { status } = await request<{ status: number }>(`/api/lists/effective/${encodeURIComponent(subject)}${q}`);
  return status;
}
